import moment from 'moment-timezone';
import { safeTimeZone } from './utility/dates.js';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib — pure engine of the unified period picker: 'YYYY-MM-DD' day keys,
 *              preset catalogue, label descriptor + formatting, ‹ › shift, month grid,
 *              draft reducers (desktop + phone). Contract: docs/period-picker/PLAN.md §2.
 *   extends:   -
 *   template:  none
 *   args:      -
 *   exports:   zoneOf, todayKey, toKey, nowOut, addDays, addMonths, daysInclusive,
 *              startOfWeek, monthRange, quarterRange, yearRange, isMonthStart, isMonthEnd,
 *              normalize, shiftRange, describeRange, formatRange, buildPresets,
 *              customPeriodRange, matchPresetId, monthGrid, initialView, pickDay, pickRange,
 *              pickUnit, pickDayPhone, previewSpan, typedEndpoint, resolveApply,
 *              toOutStart, toOutEnd
 *   uses:      moment-timezone (zone "today", locale formats, locale week numbers),
 *              safeTimeZone from ./utility/dates
 *   siblings:  ./date-parse.js (typed Dal/Al), beebee-base-template bb-period-panel /
 *              bb-period-picker / bb-period-picker-nuova, beebee-component-input bb-range-calendar
 *   tests:     tests/unit/lib/period-test.js
 *   gotchas:   keys carry NO time zone: all key arithmetic runs on UTC midnights, so DST
 *              days (2026-03-29, 2026-10-25) are plain 24h steps. A time zone is used only
 *              to turn «now» into a key (todayKey/nowOut). toKey NEVER converts zones: every
 *              value keeps the calendar date it was written with — a naive string its date
 *              part ('2026-10-14T23:59' is the 14th), an offset / Z string the date in THAT
 *              offset (parseZone), a moment its own zone's date, a Date its browser-local
 *              date. Callers hand the picker `moment().endOf('month')` / `format('…Z')`
 *              built in the browser zone; converting them into the session zone moved the
 *              day whenever the two zones disagree (the `zone` argument is kept for the
 *              API but unused).
 *              Months are 0..11 and quarters 0..3 everywhere (like moment), including the
 *              describeRange descriptor; formatRange adds 1 for display. Labels are never
 *              hardcoded: formatRange takes an injected `t` (intl-free lib). Week start
 *              `fdow` defaults to moment.localeData().firstDayOfWeek() (en-US = Sunday);
 *              week numbers are moment(key).week() in the current global locale.
 *              Reducers never mutate the draft they get; they return a new one.
 */
const KEY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const NAIVE_RE = /^(\d{4}-\d{2}-\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/;
const DAY_MS = 864e5;

// ── internal key helpers ────────────────────────────────────────────────────

function pad(n) {
  return (n < 10 ? '0' : '') + n;
}

// y, m (0..11), d -> key; overflowing m/d roll over like Date
function keyOf(y, m, d) {
  const dt = new Date(Date.UTC(y, m, d));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}
function parts(k) {
  const m = KEY_RE.exec(k);
  return {
    y: +m[1],
    m: +m[2] - 1,
    d: +m[3]
  };
}
function utc(k) {
  const p = parts(k);
  return Date.UTC(p.y, p.m, p.d);
}
function lastDay(y, m) {
  return new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
}
function weekday(k) {
  return new Date(utc(k)).getUTCDay();
}
function defaultFdow(fdow) {
  return Number.isInteger(fdow) ? fdow : moment.localeData().firstDayOfWeek();
}
function minKey(a, b) {
  if (!a) return b;
  if (!b) return a;
  return a < b ? a : b;
}
function maxKey(a, b) {
  if (!a) return b;
  if (!b) return a;
  return a > b ? a : b;
}

// ── zone / today / conversion ───────────────────────────────────────────────

function zoneOf(session) {
  return safeTimeZone(session?.me?.time_zone);
}

// `now` (optional instant) exists for tests; production callers omit it.
function todayKey(zone, now) {
  const z = safeTimeZone(zone);
  return (now === undefined ? moment.tz(z) : moment.tz(now, z)).format('YYYY-MM-DD');
}

// Day key of a period bound, in the calendar the value was WRITTEN in (see MAP gotchas):
// a moment/Date the caller built in the browser zone must not shift day in the session zone.
// eslint-disable-next-line no-unused-vars
function toKey(v, zone) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'string') {
    const naive = NAIVE_RE.exec(v.trim());
    if (naive) {
      const k = naive[1];
      const p = parts(k);
      return p.m >= 0 && p.m < 12 && p.d >= 1 && p.d <= lastDay(p.y, p.m) ? k : null;
    }
    // ISO 8601 only (no js-Date fallback, no deprecation warning); the date as written in its offset
    const m = moment.parseZone(v.trim(), moment.ISO_8601, true);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
  }
  if (moment.isMoment(v)) return v.isValid() ? v.format('YYYY-MM-DD') : null;
  // Date / epoch: the browser-local calendar date (what the caller saw when building it)
  const m = moment(v instanceof Date ? v.getTime() : v);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}
function nowOut(zone, now) {
  const z = safeTimeZone(zone);
  return (now === undefined ? moment.tz(z) : moment.tz(now, z)).format('YYYY-MM-DDTHH:mm');
}
function toOutStart(k) {
  return k ? `${k}T00:00` : null;
}
function toOutEnd(k) {
  return k ? `${k}T23:59` : null;
}

// ── key arithmetic ──────────────────────────────────────────────────────────

function addDays(k, n) {
  const p = parts(k);
  return keyOf(p.y, p.m, p.d + n);
}

// Clamped: 2026-01-31 + 1 month = 2026-02-28
function addMonths(k, n) {
  const p = parts(k);
  const first = new Date(Date.UTC(p.y, p.m + n, 1));
  const y = first.getUTCFullYear();
  const m = first.getUTCMonth();
  return keyOf(y, m, Math.min(p.d, lastDay(y, m)));
}
function daysInclusive(a, b) {
  return Math.round((utc(b) - utc(a)) / DAY_MS) + 1;
}
function startOfWeek(k, fdow) {
  const f = defaultFdow(fdow);
  return addDays(k, -((weekday(k) - f + 7) % 7));
}
function monthRange(y, m) {
  return [keyOf(y, m, 1), keyOf(y, m, lastDay(y, m))];
}
function quarterRange(y, q) {
  return [keyOf(y, q * 3, 1), keyOf(y, q * 3 + 2, lastDay(y, q * 3 + 2))];
}
function yearRange(y) {
  return [keyOf(y, 0, 1), keyOf(y, 11, 31)];
}
function isMonthStart(k) {
  return parts(k).d === 1;
}
function isMonthEnd(k) {
  const p = parts(k);
  return p.d === lastDay(p.y, p.m);
}
function normalize(f, t) {
  if (f && t && t < f) return [t, f];
  return [f, t];
}

// ‹ ›: whole months (also quarters, years, multi-month spans) move by their month count,
// anything else by its own length in days. An open range cannot be stepped -> null.
function shiftRange(f, t, dir) {
  if (!f || !t) return null;
  if (isMonthStart(f) && isMonthEnd(t)) {
    const a = parts(f);
    const b = parts(t);
    const n = (b.y - a.y) * 12 + b.m - a.m + 1;
    return [keyOf(a.y, a.m + dir * n, 1), keyOf(b.y, b.m + dir * n + 1, 0)];
  }
  const len = daysInclusive(f, t);
  return [addDays(f, dir * len), addDays(t, dir * len)];
}

// ── label ───────────────────────────────────────────────────────────────────

function describeRange(f, t, today) {
  const from = f || null;
  const to = t || null;
  const base = {
    from,
    to,
    year: null,
    month: null,
    quarter: null,
    sameYear: false
  };
  if (!from && !to) return {
    ...base,
    kind: 'all'
  };
  if (!from) return {
    ...base,
    kind: 'until',
    year: parts(to).y
  };
  if (!to) return {
    ...base,
    kind: 'from',
    year: parts(from).y
  };
  const a = parts(from);
  const b = parts(to);
  const sameYear = a.y === b.y;
  const desc = {
    ...base,
    year: a.y,
    month: a.m,
    sameYear
  };
  if (from === to) {
    if (today && from === today) return {
      ...desc,
      kind: 'today'
    };
    if (today && from === addDays(today, -1)) return {
      ...desc,
      kind: 'yesterday'
    };
    return {
      ...desc,
      kind: 'day'
    };
  }
  if (sameYear && isMonthStart(from) && isMonthEnd(to)) {
    if (a.m === 0 && b.m === 11) return {
      ...desc,
      kind: 'year',
      month: null
    };
    if (a.m === b.m) return {
      ...desc,
      kind: 'month'
    };
    if (a.m % 3 === 0 && b.m === a.m + 2) return {
      ...desc,
      kind: 'quarter',
      quarter: a.m / 3
    };
  }
  return {
    ...desc,
    kind: 'range'
  };
}
function fmtKey(k, format, locale) {
  const m = moment(k, 'YYYY-MM-DD', true);
  if (locale) m.locale(locale);
  return m.format(format);
}
function capitalise(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// `t(key, params)` is injected (intl.t); `locale` optionally pins the moment locale,
// otherwise the current global moment locale is used.
function formatRange(desc, {
  t,
  locale
} = {}) {
  const tr = typeof t === 'function' ? t : k => k;
  const day = k => fmtKey(k, 'D MMM YYYY', locale);
  if (!desc) return '';
  switch (desc.kind) {
    case 'all':
      return tr('period_picker.preset.all');
    case 'until':
      return tr('period_picker.label.until', {
        date: day(desc.to)
      });
    case 'from':
      return tr('period_picker.label.from', {
        date: day(desc.from)
      });
    case 'today':
      return tr('period_picker.preset.today');
    case 'yesterday':
      return tr('period_picker.preset.yesterday');
    case 'day':
      return day(desc.from);
    case 'month':
      return capitalise(fmtKey(desc.from, 'MMMM YYYY', locale));
    case 'quarter':
      return tr('period_picker.label.quarter', {
        quarter: desc.quarter + 1,
        year: desc.year
      });
    case 'year':
      return String(desc.year);
    default:
      return tr('period_picker.label.range', {
        from: desc.sameYear ? fmtKey(desc.from, 'D MMM', locale) : day(desc.from),
        to: day(desc.to)
      });
  }
}

// ── presets ─────────────────────────────────────────────────────────────────

// moment.normalizeUnits() name -> whole-day step. Units finer than a day (hour, minute…) or
// unknown names have no day meaning: customEnd returns null and the preset is not offered.
const DAY_UNITS = {
  day: (k, n) => addDays(k, n),
  week: (k, n) => addDays(k, n * 7),
  isoWeek: (k, n) => addDays(k, n * 7),
  month: (k, n) => addMonths(k, n),
  quarter: (k, n) => addMonths(k, n * 3),
  year: (k, n) => addMonths(k, n * 12)
};
function customEnd(today, date, add, addPeriod) {
  // Port of the legacy calculateDate (`moment().add(add, addPeriod || 'days')`): a set `*_add`
  // (0 included) is relative to today, otherwise `date` is an absolute 'YYYY-MM-DD'.
  if (add || add === 0) {
    const n = Number(add);
    if (!Number.isFinite(n)) return null;
    const unit = addPeriod ? moment.normalizeUnits(String(addPeriod)) : 'day';
    const step = unit ? DAY_UNITS[unit] : null;
    return step ? step(today, n) : null;
  }
  return toKey(date);
}
function customPeriodRange(period, today) {
  if (!period) return [null, null];
  return [customEnd(today, period.from_date, period.from_date_add, period.from_date_add_period), customEnd(today, period.to_date, period.to_date_add, period.to_date_add_period)];
}
function item(id, labelKey, [from, to], legacyHook = null) {
  return {
    id,
    labelKey,
    from,
    to,
    legacyHook
  };
}
function buildPresets({
  today,
  fdow,
  showAllTimes,
  pastFuture,
  customPeriods
} = {}) {
  const p = parts(today);
  const sow = startOfWeek(today, fdow);
  const lm = addMonths(keyOf(p.y, p.m, 1), -1);
  const lmp = parts(lm);
  const groups = [{
    id: 'quick',
    labelKey: 'period_picker.group.quick',
    items: [item('today', 'period_picker.preset.today', [today, today], 'set-fix'), item('yesterday', 'period_picker.preset.yesterday', [addDays(today, -1), addDays(today, -1)], 'set-yesterday'), item('l7', 'period_picker.preset.last_7', [addDays(today, -6), today]), item('l30', 'period_picker.preset.last_30', [addDays(today, -29), today])]
  }, {
    id: 'calendar',
    labelKey: 'period_picker.group.calendar',
    items: [item('week', 'period_picker.preset.this_week', [sow, addDays(sow, 6)], 'set-fix-2'), item('last-week', 'period_picker.preset.last_week', [addDays(sow, -7), addDays(sow, -1)]), item('month', 'period_picker.preset.this_month', monthRange(p.y, p.m), 'set-fix-3'), item('last-month', 'period_picker.preset.last_month', monthRange(lmp.y, lmp.m), 'set-last-month'), item('quarter', 'period_picker.preset.this_quarter', quarterRange(p.y, Math.floor(p.m / 3))), item('year', 'period_picker.preset.this_year', yearRange(p.y), 'set-fix-4'), item('last-year', 'period_picker.preset.last_year', yearRange(p.y - 1), 'set-last-year')]
  }];
  const open = [];
  if (pastFuture && showAllTimes !== false) {
    open.push(item('past', 'period_picker.preset.until_today', [null, today], 'set-pas-fut'));
    open.push(item('future', 'period_picker.preset.from_today', [today, null], 'set-pas-fut-2'));
  }
  if (showAllTimes !== false) {
    open.push(item('all', 'period_picker.preset.all', [null, null], 'select-all'));
  }
  if (open.length) groups.push({
    id: 'open',
    labelKey: 'period_picker.group.open',
    items: open
  });
  const custom = (Array.isArray(customPeriods) ? customPeriods : []).filter(Boolean).map((period, i) => {
    const [from, to] = customPeriodRange(period, today);
    return {
      id: `custom-${i}`,
      label: period.name,
      from,
      to,
      legacyHook: 'set-custom'
    };
  })
  // A bound that does not resolve (unknown unit, bad date) is not offered: pressed, it would
  // silently apply an open or wrong range (legacy sent «Invalid date»). Ids are given before
  // this filter, so a dropped period does not renumber the others.
  .filter(p => p.from && p.to);
  if (custom.length) groups.push({
    id: 'custom',
    labelKey: 'period_picker.group.custom',
    items: custom
  });
  return groups;
}
function matchPresetId(groups, f, t) {
  const from = f || null;
  const to = t || null;
  for (const g of groups || []) {
    for (const p of g.items || []) {
      if ((p.from || null) === from && (p.to || null) === to) return p.id;
    }
  }
  return null;
}

// ── grid / view ─────────────────────────────────────────────────────────────

function monthGrid(y, m, fdow) {
  const f = defaultFdow(fdow);
  const n = lastDay(y, m);
  const first = keyOf(y, m, 1);
  const lead = (weekday(first) - f + 7) % 7;
  const weeks = [];
  for (let r = 0; r < 6; r++) {
    const from = addDays(first, r * 7 - lead);
    const days = [];
    let any = false;
    for (let c = 0; c < 7; c++) {
      const d = r * 7 + c - lead + 1;
      if (d >= 1 && d <= n) {
        any = true;
        days.push({
          key: keyOf(y, m, d),
          day: d,
          inMonth: true
        });
      } else {
        days.push({
          key: null,
          day: null,
          inMonth: false
        });
      }
    }
    if (!any) continue;
    weeks.push({
      weekNo: moment(from, 'YYYY-MM-DD', true).week(),
      from,
      to: addDays(from, 6),
      days
    });
  }
  return {
    weeks
  };
}

// Month the calendar opens on. Two months: the first month of the range when the range
// spans months, otherwise the month before (so the range sits in the right-hand month).
// One month: the month of the start (or end, or today).
function initialView(f, t, today, months = 2) {
  const base = parts(f || t || today);
  if (months === 1) return {
    year: base.y,
    month: base.m
  };
  if (f && t) {
    const b = parts(t);
    if (b.y * 12 + b.m !== base.y * 12 + base.m) return {
      year: base.y,
      month: base.m
    };
  }
  const prev = new Date(Date.UTC(base.y, base.m - 1, 1));
  return {
    year: prev.getUTCFullYear(),
    month: prev.getUTCMonth()
  };
}

// ── draft reducers: draft = { from, to, pendingEnd, unit, editing } ──────────

function draftOf(d, patch) {
  return {
    from: null,
    to: null,
    pendingEnd: null,
    unit: null,
    editing: 'from',
    ...(d || {}),
    ...patch
  };
}

// Desktop day click: first click starts a range, second click closes it (either side).
function pickDay(d, k) {
  if (!d?.from || d.to) return draftOf(d, {
    from: k,
    to: null,
    pendingEnd: null,
    unit: 'days'
  });
  if (k < d.from) return draftOf(d, {
    from: k,
    to: d.from,
    pendingEnd: null,
    unit: null
  });
  return draftOf(d, {
    from: d.from,
    to: k,
    pendingEnd: null,
    unit: null
  });
}

// Whole-range pick (week number, month title, quarter row, phone month, typed pair).
function pickRange(d, f, t) {
  const [from, to] = normalize(f || null, t || null);
  return draftOf(d, {
    from,
    to,
    pendingEnd: null,
    unit: null,
    editing: 'from'
  });
}

// Month / year tile click: the first tile is pending (start + pendingEnd), a second tile
// of the same unit closes the range covering both; a tile of another unit restarts.
function pickUnit(d, f, t, unit) {
  if (!d?.from || d.to || d.unit !== unit) {
    return draftOf(d, {
      from: f,
      to: null,
      pendingEnd: t,
      unit
    });
  }
  return draftOf(d, {
    from: minKey(d.from, f),
    to: maxKey(d.pendingEnd || d.from, t),
    pendingEnd: null,
    unit: null
  });
}

// Phone: the edited endpoint (Dal/Al box) decides what a tap sets.
function pickDayPhone(d, k) {
  if (!d?.from || d.editing === 'from') {
    const to = d?.to && d.to >= k ? d.to : null;
    return draftOf(d, {
      from: k,
      to,
      pendingEnd: null,
      unit: null,
      editing: 'to'
    });
  }
  if (k < d.from) return draftOf(d, {
    from: k,
    to: d.from,
    pendingEnd: null,
    unit: null,
    editing: 'from'
  });
  return draftOf(d, {
    to: k,
    pendingEnd: null,
    unit: null,
    editing: 'from'
  });
}

// The span to paint. `hover` is a key, or in months/years mode either the tile key the
// prototype passes (tile start when before `from`, else tile end) or a [tileFrom, tileTo]
// pair, which is resolved the same way here.
function hoverKey(hover, from) {
  if (!hover) return null;
  if (!Array.isArray(hover)) return hover;
  return from && hover[0] < from ? hover[0] : hover[1];
}
function previewSpan(d, hover, mode = 'days') {
  const from = d?.from || null;
  const to = d?.to || null;
  if (!from || to) return [from, to || from];
  const h = hoverKey(hover, from);
  if (mode !== 'days') {
    const pe = d.pendingEnd || from;
    if (!h) return [from, pe];
    return h < from ? [h, pe] : [from, maxKey(h, pe)];
  }
  if (!h) return [from, from];
  return h < from ? [h, from] : [from, h];
}

// A typed Dal/Al value (already parsed to a key, or null for an emptied field).
// `undefined` = unparseable: the draft is returned unchanged (the field shows the error).
function typedEndpoint(d, which, key) {
  if (key === undefined) return draftOf(d, {});
  let from = which === 'from' ? key : d?.from || null;
  let to = which === 'to' ? key : d?.to || null;
  [from, to] = normalize(from, to);
  return draftOf(d, {
    from,
    to,
    pendingEnd: null,
    unit: null
  });
}

// Applica: start only = that day (or the pending tile's end); null/null = all.
function resolveApply(d) {
  const from = d?.from || null;
  const to = d?.to || null;
  if (from && !to) return [from, d.pendingEnd || from];
  return normalize(from, to);
}

export { addDays, addMonths, buildPresets, customPeriodRange, daysInclusive, describeRange, formatRange, initialView, isMonthEnd, isMonthStart, matchPresetId, monthGrid, monthRange, normalize, nowOut, pickDay, pickDayPhone, pickRange, pickUnit, previewSpan, quarterRange, resolveApply, shiftRange, startOfWeek, toKey, toOutEnd, toOutStart, todayKey, typedEndpoint, yearRange, zoneOf };
//# sourceMappingURL=period.js.map
